import type {
    Automation,
    Experience,
    ExperienceVersion,
    UpdateEngageV4Input,
    TrafficModeType
} from '@models/experience/experience';
import numberUtils from '@utils/number.utils';
import urlUtils from '@utils/url.utils';
import uuidUtils from '@utils/uuid.utils';
import { cloneDeep, isEqual } from 'lodash-es';
import { schedueDefaultMeta } from '@experience/config/setting.config';
import router from '@router/router';
import dayjs from 'dayjs';
import i18n from '@assets/i18n';

// experience setting页面的一些方法

/**
 * 统一处理给rate / 100
 * @param versions
 * @returns
 */
export function unFormatVersionsRate(versions: ExperienceVersion[]) {
    if (!versions || versions.length <= 0) {
        return versions;
    }
    versions.map((v: ExperienceVersion) => {
        v.rate = numberUtils.strip(v.rate / 100);
    });
    return versions;
}

/**
 * 统一处理version rate * 100
 * @param versions
 * @returns
 */
export function formatVersionsRate(versions: ExperienceVersion[]) {
    if (!versions || versions.length <= 0) {
        return versions;
    }
    versions.map((v: ExperienceVersion) => {
        v.rate = numberUtils.strip(v.rate * 100);
    });
    return versions;
}

/**
 * 初始化experience创建参数
 * @param param0
 * @returns
 */
export function initExperience({
    timezone,
    commonUsergroupId
}: {
    timezone?: string;
    commonUsergroupId: string;
}) {
    return {
        name: '',
        engageType: '',
        antiFlicker: false,
        isABTest: false,
        conditions: [
            {
                when: []
            }
        ],
        schedule: [
            {
                timezone: {
                    profile: timezone,
                    useVisitor: false
                },
                versionData: [],
                versionMeta: cloneDeep(schedueDefaultMeta)
            }
        ],
        usergroups: [
            {
                type: 'USER_GROUP',
                value: [commonUsergroupId]
            }
        ],
        trigger: {
            start: [],
            stop: [],
            frequency: {
                type: 'default'
            }
        }
    };
}

/**
 * 判断experience是否有inline
 * @param experienceInfo
 * @returns
 */
export const experienceHasInline = (experienceInfo: Experience) => {
    return (experienceInfo?.versions || []).some(v => {
        const { controlGroup, terminals } = v || {};
        return (
            !controlGroup &&
            (terminals || []).some(t => {
                const { status, popup, stickybar } = t || {};
                const hasError =
                    status === 1 &&
                    [...(popup || []), ...(stickybar || [])].some(layout => layout.popupHasError);
                return t.status === 1 && !hasError && (t?.inline || []).length > 0;
            })
        );
    });
};

/**
 * 判断experience是否设置了schedule
 * @param experienceInfo
 * @returns
 */
export const experienceHasSchedule = (experienceInfo: Experience) => {
    return (
        experienceInfo.schedule?.[0].versionMeta.start === '2' &&
        experienceInfo.status !== 'RUNNING'
    );
};

/**
 * experience的计算属性  usergroup换算
 * @param experienceInfo
 * @returns
 */
export function formatExperienceInfo(experienceInfo: Experience) {
    if (!experienceInfo) {
        return null;
    }
    const experience = cloneDeep(experienceInfo);
    const { usergroups, versions, conditions = [], engageType } = experience;
    // 如果当前是重定向测试
    if (versions?.length > 0 && engageType === 'REDIRECT') {
        const controlGroup = experience.versions.find((item) => item.controlGroup);
        if (controlGroup) {
            const terminal = controlGroup.terminals?.[0]?.redirect ?? {};
            const params = urlUtils.getUrlParams(terminal.toValue) ?? {};
            Object.keys(params).forEach((key) => {
                const value = params?.[key] ? `=${params[key]}` : '';
                const item = {
                    type: 'URL',
                    condition: '[()]',
                    uuid: uuidUtils.uuid(),
                    value: [`${key}${value}`]
                };
                if (conditions?.[0]?.when) {
                    conditions[0].when.push(item);
                } else {
                    conditions[0].when = [item];
                };
            });
            terminal.toValue = urlUtils.getProtocolAndHostNameFromUrl(terminal.toValue);
            terminal.previewUrl = urlUtils.getProtocolAndHostNameFromUrl(terminal.previewUrl);
        }
    }
    if (usergroups?.length > 0) {
        const result: any = [];
        usergroups.map(item => {
            if (item.type === 'CONDITION') {
                result.push(item.value);
            } else if (item.type === 'USER_GROUP') {
                result.push({
                    type: 'userGroup',
                    value: item.value?.map((v: { code?: any; id: any }) => {
                        return v.id;
                    })
                });
            }
        });
        experience.usergroups = result;
    }
    return experience;
}

/**
 * 判断是否有内容更新
 * @param params
 * @param experienceInfo
 * @returns
 */
export function judgeHasSync(params: UpdateEngageV4Input, experienceInfo: Experience) {
    let hasUpdate = false;
    // goals, experience name,  version name 更改不算在内
    if (!params || !experienceInfo) {
        return false;
    }
    Object.keys(params).map((key: any) => {
        if (key === 'versions') {
            const newVersions = params.versions;
            const originVersions = experienceInfo.versions;
            if (newVersions?.length !== originVersions?.length) {
                hasUpdate = true;
            } else {
                newVersions?.map((v, vi) => {
                    const originVersion = originVersions?.find(o => o.id === v.id);
                    originVersion &&
                        Object.keys(v).map(vk => {
                            if (!isEqual(v[vk], originVersion[vk]) && vk !== 'name') {
                                hasUpdate = true;
                            }
                        });
                });
            }
        } else if (
            !isEqual(params[key], experienceInfo[key]) &&
            !['goals', 'name', 'calculateDays', 'calculateConfidence'].includes(key)
        ) {
            hasUpdate = true;
        }
    });
    return hasUpdate;
}

/**
 * 平均分配版本rate
 * @param versions
 * @param trafficMode
 * @returns
 */
export function averageVersionsRateFn(
    versions: ExperienceVersion[] | undefined,
    trafficMode?: TrafficModeType
): ExperienceVersion[] {
    if (!versions || !versions.length) {
        return;
    }
    if (['AVERAGE', 'BANDIT'].includes(trafficMode || 'AVERAGE')) {
        const clonedVersions = formatVersionsRate(cloneDeep(versions));
        const index: number = clonedVersions.length;
        // 平均分配模式计算逻辑：流量按版本平均分配。如果不能整除版本，从最后版本开始分配余数
        // 示例：7版本：14% 14% 14% 14% 14% 15% 15%
        let remainder = 100 % index;
        const divideIntRate = numberUtils.strip(parseInt(100 / index + ''));
        for (let i = index - 1; i >= 0; i--) {
            clonedVersions[i].rate = remainder > 0 ? divideIntRate + 1 : divideIntRate;
            remainder--;
        }
        return unFormatVersionsRate(clonedVersions);
    }
    return versions;
}

/**
 * 自定义trafficMode 按比例分配
 * @param versions
 * @param pauseRate
 * @returns
 */
export function divideVersionRateByproportion(versions: ExperienceVersion[], pauseRate: number) {
    if (!versions || pauseRate === undefined) {
        return versions;
    }
    const cloneVersions = formatVersionsRate(cloneDeep(versions));
    const valideCloneVersions = cloneVersions?.filter(c => c.rate);
    const pauseRate100 = pauseRate * 100;
    const len: number = valideCloneVersions.length;
    let remainNormalRate = 0;
    valideCloneVersions.map((v, index) => {
        if (index !== len - 1) {
            v.rate = numberUtils.strip(
                v.rate + Math.floor((v.rate / (100 - pauseRate100)) * pauseRate100)
            );
            remainNormalRate += v.rate;
        } else {
            v.rate = numberUtils.strip(100 - remainNormalRate);
        }
        return v;
    });
    return unFormatVersionsRate(cloneVersions);
}

/**
 * 处理shchedule 历史数据 startDate => startDateStr  stopDate => stopDateStr
 * @param versionMeta
 * @returns
 */
export function initScheduleData(versionMeta: any) {
    if (!versionMeta) {
        return null;
    }
    const { startDate, stopDate, startDateStr, stopDateStr } = versionMeta;
    if (!startDateStr || !stopDateStr) {
        versionMeta.startDateStr = dayjs(startDate).format('YYYY-MM-DD HH:mm:ss');
        versionMeta.stopDateStr = dayjs(stopDate).format('YYYY-MM-DD HH:mm:ss');
    }
    return versionMeta;
}

/**
 *  处理usergroup历史数据 visitPage entryPage 没有subType值得 默认给page
 * @param usergroups
 * @returns
 */
export function initUsergroupData(usergroups: any) {
    if (!usergroups) {
        return null;
    }
    const cloneUsergroups = cloneDeep(usergroups);
    if (cloneUsergroups?.length > 0) {
        cloneUsergroups.map((item: any) => {
            if (['visitPage', 'entryPage'].includes(item.type) && !item.subType) {
                item.subType = 'page';
            }
        });
    }
    return cloneUsergroups;
}

export function getVersionNameNum(versions: ExperienceVersion[]) {
    const reg = /(\d+)$/;
    let nameNum = 0;
    versions
        .filter(v => !v.controlGroup)
        .forEach(v => {
            const count = reg.exec(v.name);
            count && (nameNum = Math.max(parseInt(count), nameNum));
        });
    return nameNum;
}

/**
 * 获得自增1的version名字
 */
export function getNewVersionName(version?: ExperienceVersion, nameNum = 0): string {
    const reg = /(\d+)$/;
    const defaultName = i18n.global.t('experience.setting.version_default_name');
    const count = reg.exec(version?.name || '');
    let name: string = defaultName;

    if (version?.controlGroup) {
        name = i18n.global.t('experience.setting.control_group') as string;
    } else if (count) {
        name = `${version?.name.trim().replace(count[0], '')}${nameNum + 1}`;
    }
    return name;
}

/**
 * 跳转setting页面
 */
export function routerChangeToSetting(id?: string) {
    const back: any = router.currentRoute.value?.name || 'ExperienceList';
    return function () {
        router.push({
            name: 'ExperienceSetting',
            params: {
                experienceId: id
            },
            query: {
                back
            }
        });
    };
}

export function getEachVersionHasRequiredError(experienceInfo: Experience, formData): string[] {
    // 根据已经选择字段并且映射的是邮箱字段来判断每个版本下的每个终端至少有一个popup或者stickybar存在映射了邮箱的字段
    const TYPE_SHOPIFY_EMAIL = 'email';
    // 错误的集合
    const errors = [];
    const ids = formData.reduce(
        (acc, item) => (item.shopify === TYPE_SHOPIFY_EMAIL ? [...acc, ...item.id] : acc),
        []
    );
    // 还没选择邮箱报错
    if (!ids.length) {
        //当前shopify未选择邮箱字段
        errors.push(i18n.global.t('experience.automation.no_email_field'));
    } else {
        // 做终端文案映射
        const TERMINALS_MAP = { 1: 'SP', 2: 'PC' } as const;
        // 获取版本和当前是否开启了A/B测
        const { versions, enableABTest } = experienceInfo;
        // 获取所有的每个版本下的所有终端
        const versionUnderAllTerminals = versions.reduce((acc, item) => {
            // 将所有开启的终端进行合并将控制组和流量分配为0的过滤掉，不参与校验
            if (item.controlGroup || item.rate === 0) {
                return acc;
            }
            // 找到版本下的终端，并且过滤终端下无表单的情况
            const addVersionIdIntoItem = item.terminals.reduce((prevTerminal, nextTerminal) => {
                // 将popup和stickybar取并集
                const { popup = [], stickybar = [], status, id, type } = nextTerminal;
                const popUpAndStickyBar = [...popup, ...stickybar];
                // 保证每个终端至少有一个popup有表单
                const isHasAFormInAllPopupOrStickyBar = popUpAndStickyBar.some(
                    element => element.popupHasForm
                );
                // 取开启的终端并且当前的popup或者stickybar存在表单
                if (status === 1 && isHasAFormInAllPopupOrStickyBar) {
                    return [
                        ...prevTerminal,
                        {
                            terminalId: id,
                            terminalType: type,
                            status: status,
                            versionName: item.name,
                            versionId: item.id
                        }
                    ];
                }
                return prevTerminal;
            }, []);
            return [...acc, addVersionIdIntoItem];
        }, []);
        // 生成一个终端名字的映射表
        const versionNameForTerminalsMap = {};
        // 遍历所有终端下的popup和stickybar
        versionUnderAllTerminals.forEach(terminals => {
            // 遍历每个终端判断当前的已经选择的id是否和当前版本关联
            terminals.forEach(terminal => {
                const target = ids.find(id =>
                    id.includes(`${terminal.versionId}&${terminal.terminalId}`)
                );
                // 如果不关联则将具体版本进行保存
                if (!target) {
                    const type = TERMINALS_MAP[terminal.terminalType];
                    versionNameForTerminalsMap[terminal.versionName]
                        ? versionNameForTerminalsMap[terminal.versionName].push(type)
                        : (versionNameForTerminalsMap[terminal.versionName] = [type]);
                }
            });
        });
        // 遍历当前的信息最后生成字符串
        if (Object.keys(versionNameForTerminalsMap).length) {
            const result = [];
            Object.keys(versionNameForTerminalsMap).forEach(key => {
                const value = versionNameForTerminalsMap[key];
                const stringValue = value.length ? `（${value.join('&')}）` : '';
                const abTestKey = enableABTest ? key : '';
                const string = `${abTestKey}${stringValue}`;
                result.push(string);
            });
            const version = result.join(',');
            errors.push(i18n.global.t('experience.automation.no_each_email_field', { version }));
        }
    }
    return errors;
}

export function checkAndUpdateExperienceVersionFormFields(
    experienceInfo: Experience
): null | Automation {
    // 表单上的字段, 如果当前automations不存在，也不需要进行对比，可能是用户关闭了自动化，也可能是初始化情况
    if (experienceInfo?.automations?.length) {
        // 拿到所有字段集合
        const options = createIntegrationShopifyFields<Experience, false>(experienceInfo, false);
        const automations = experienceInfo?.automations ?? [];
        const { actions } =
            automations?.find(item => item.pushBusinessType === 'WHEN_SUBMIT_FORM') ?? {};
        const action = actions?.find(item => item.platform === 'SHOPIFY');
        // 拿到当前用户选择得所有字段
        const fieldMapping = action?.config?.fieldMapping ?? [];
        // 当前映射配置的shopify链接的ID
        const connectionID = action?.config.connectionID;
        let isUpdate = false;
        if (fieldMapping.length) {
            options.forEach(option => {
                const { name, id = [] } = option;
                const targetFieldByName = fieldMapping.find(item => item?.ptengine === name);
                const targetFieldById = fieldMapping.find(item => item?.id?.includes(id));
                // 同一个名字且id在当前选择的集合内,如果同名字且不在集合中存在，说明是新增加的一个同名字段
                // name 和 id 都存在说明是已经选择过的字段
                // name没找到，id也没在映射表中找到，是一个没被选择过的字段
                if (
                    targetFieldByName &&
                    targetFieldById &&
                    targetFieldById.ptengine !== targetFieldByName.ptengine
                ) {
                    // 同时存在，但是名字不同要以名字为主，删除找到的id
                    const index = fieldMapping.findIndex(item => item?.id?.includes(id));
                    index >= 0 && fieldMapping.splice(index, 1);
                    isUpdate = true;
                }
                if (!targetFieldByName && targetFieldById) {
                    // 没找到名字，但是在映射表中找到了相关的id，是编辑器将已经有的字段改了名字
                    if (targetFieldById.id.length === 1) {
                        targetFieldById.ptengine = name;
                    } else {
                        // 找到对应的id说明是改过名字的一个字段并且删除这个已经存在的在生成基于这个id新的一个mapping
                        const indexId = targetFieldById?.id?.findIndex(i => i === id);
                        indexId >= 0 && targetFieldById?.id?.splice(indexId, 1);
                        fieldMapping.push({
                            ptengine: name,
                            shopify: targetFieldById.shopify,
                            id: [id],
                            shopifyErrorInfo: '',
                            ptengineErrorInfo: ''
                        });
                    }
                    isUpdate = true;
                }
                // 如果名字找到但是id没找到说明是一个新加的字段直接将id保存，最后通过id的集合判断是更新还是删除
                if (targetFieldByName && !targetFieldById) {
                    if (Array.isArray(targetFieldByName?.id)) {
                        // 如果这里存在这个id则不将当前id存入这里
                        !targetFieldByName?.id.includes(id) && targetFieldByName?.id.push(id);
                    } else if (targetFieldByName?.id) {
                        targetFieldByName.id = [id];
                    }
                    isUpdate = true;
                }
            });
            // 判断字段是否删除
            fieldMapping.forEach(field => {
                if (options.every(option => !field?.id?.includes(option.id))) {
                    field.ptengineErrorInfo = i18n.global.t(
                        'experience.automation.field_deleted'
                    ) as string;
                } else {
                    field.ptengineErrorInfo = '';
                }
            });
            // 是否同步更新标记位，构建automations后判断是否同步更新
            if (isUpdate) {
                const automations = [
                    {
                        pushBusinessType: 'WHEN_SUBMIT_FORM',
                        actions: [
                            {
                                platform: 'SHOPIFY',
                                config: {
                                    connectionID: connectionID,
                                    fieldMapping: fieldMapping
                                }
                            }
                        ]
                    }
                ];
                return automations;
            }
        }
    }
    return null;
}
/**
 * 创建json格式数据
 */
function createJsonFromElement(element: string, info) {
    const dom = document.createElement('div');
    const { versionId, versionName, terminalId, terminalType, popupId } = info;
    dom.innerHTML = element;
    const field = [];
    // 找到所有带data-ptx-property属性的元素并将其属性组成相应的数据结构
    const fields = dom.querySelectorAll<HTMLDivElement>('div[data-silex-type=form-element]');
    for (let i = 0; i < fields.length; i++) {
        const fieldElement = fields[i];
        const label = fieldElement.querySelector('label');
        const componentElement =
            fieldElement.querySelector<HTMLInputElement>('[data-ptx-property]');
        const text = label?.innerText;
        const { name = null } = componentElement ?? {};
        const ptxProperty = componentElement?.dataset?.ptxProperty ?? '';
        const ptxValidators = componentElement?.dataset?.ptxValidators;
        // ptxValidators可能是可空字符串，如果是这样默认为false
        const validatior = ptxValidators ? JSON.parse(ptxValidators) : {};
        const required = validatior?.required ?? false;
        // 将版本id 终端id 弹窗类型的id - popup和stickybar的id使用的同一个字段都叫popupId;
        field.push({
            id: `${versionId}&${terminalId}&${popupId}&${fieldElement.dataset.fieldId}`,
            versionName,
            versionId,
            name: text,
            required: required,
            componentType: name,
            fieldType: fieldElement?.dataset.ptxComponentid,
            componentName: ptxProperty,
            terminalType
        });
    }
    return field;
}
/**
 * 将当前数据进行平铺并合并同一个字段名字的字段进行信息汇总
 */
function mergeSameNameBundles(bundles) {
    // 合并同名字段，区分终端信息
    const mergeResult = bundles.reduce((acc, next) => {
        const target = acc.find(item => item.name === next.name);
        const versionInfo = {
            versionId: next.versionId,
            versionName: next.versionName,
            terminalId: next.terminalId
        };
        if (target) {
            target.id.push(next.id);
            const usedVersions = target?.usedVersions;
            // 找到当前版本号
            const usedVersion = usedVersions?.find(({ versionId }) => versionId === next.versionId);
            if (usedVersion && usedVersion.terminalType !== next.terminalType) {
                // 存在版本好更新当前的终端信息
                usedVersion.terminalType += '&' + next.terminalType;
            } else {
                usedVersions?.push({
                    ...versionInfo,
                    terminalType: next.terminalType
                });
            }
            return acc;
        }
        return acc.concat({
            ...next,
            id: [next.id],
            usedVersions: [
                {
                    ...versionInfo,
                    terminalType: next.terminalType
                }
            ]
        });
    }, []);
    //处理后面根据id获取的版本和终端设置必填非必填
    mergeResult.forEach(item => {
        item.requiredSets = [];
        item.fieldTypeSets = [];
        item.id.forEach(id => {
            const targetById = bundles.find(it => it.id === id);
            if (targetById) {
                item.requiredSets.push({
                    versionNo: targetById.versionNo,
                    terminalType: targetById.terminalType,
                    required: targetById.required
                });
                item.fieldTypeSets.push({
                    versionNo: targetById.versionNo,
                    terminalType: targetById.terminalType,
                    fieldType: targetById.fieldType
                });
            }
        });
    });
    return mergeResult;
}

export function createIntegrationShopifyFields(experienceInfo: Experience, isMerge = true) {
    const TERMINALS_MAP = { 1: 'SP', 2: 'PC' } as const;
    const { versions } = experienceInfo ?? {};
    const bundles = [];
    // 存在版本则向下查找终端
    if (versions) {
        for (let i = 0; i < versions.length; i++) {
            const version = versions[i];
            const { terminals, id: versionId, name: versionName } = version;
            // 查找终端下的所有popup和stickybar
            for (let j = 0; j < terminals.length; j++) {
                const terminal = terminals[j];
                const { status, id: terminalId } = terminal;
                // 关闭的终端不收集字段
                if (status) {
                    const terminalType = TERMINALS_MAP[terminal.type as 1 | 2];
                    const { popup = [], stickybar = [] } = terminal;
                    const engages = [...popup, ...stickybar];
                    for (let l = 0; l < engages.length; l++) {
                        const engage = engages[l];
                        // 目前只考虑一种情况
                        const widgetNodes = engage?.widgets?.[0]?.widgetNodes ?? {};
                        const { popupId, popupName } = engage;
                        const html = widgetNodes?.html ?? '';
                        const info = {
                            terminalType,
                            versionId,
                            versionName,
                            terminalId,
                            popupId,
                            popupName
                        };
                        if (html) {
                            bundles.push(...createJsonFromElement(html, info));
                        }
                    }
                }
            }
        }
    }
    return isMerge ? mergeSameNameBundles(bundles) : bundles;
}
